import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ScopeType } from '@seahorse/domain';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ShTableComponent } from '@seahorse/shell';
import type { ShColumn } from '@seahorse/shell';
import { SeahorseModuleService } from '../../services/seahorse-module.service';
import { ProxyServices } from '../../../../shared/services/proxy.service';

/** One row of the module list: a Seahorse module with its translated object name. */
export interface SeahorseModuleRow {
  id: number;
  isActive: boolean;
  name: string;
}

/**
 * The Seahorse modules of the organisation picked on the overview page (the
 * child route of the overview): active flag and Seahorse object, a row opens
 * the module's details page.
 */
@Component({
  selector: 'su-seahorse-module-list',
  templateUrl: './seahorse-module-list.component.html',
  imports: [CommonModule, TranslateModule, ShTableComponent],
})
export class SeahorseModuleListComponent implements OnInit, OnDestroy {
  organisationId: string;
  organisationName: string;

  // Stable fields: they feed sh-table.
  rows: SeahorseModuleRow[] = [];
  readonly columns: ShColumn<SeahorseModuleRow>[] = [
    { key: 'isActive', header: 'shared.statuses.active', type: 'boolean' },
    { key: 'name', header: 'seahorse.object' },
  ];

  isLoading = false;

  private _subscription = new Subscription();

  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _translateService = inject(TranslateService);
  private _seahorseModuleService = inject(SeahorseModuleService);
  private _proxyService = inject(ProxyServices);

  ngOnInit(): void {
    this._subscription.add(
      this._route.paramMap.subscribe((params) => {
        this.organisationId = params.get('organisationId');
        this.organisationName = history.state.organisationName;

        this.load();
      })
    );
  }

  openModule(row: SeahorseModuleRow) {
    this._router.navigate(['/seahorse-modules/details', row.id]);
  }

  /** Loads the Seahorse object definitions of the organisation, then its modules. */
  private load(): void {
    this.isLoading = true;
    this.rows = [];
    this._proxyService.organisationId = this.organisationId;

    this._subscription.add(
      this._proxyService.getData().subscribe(() => {
        const objects = (this._proxyService.objectDefinitions ?? []).filter(
          (x) => x.scope === ScopeType.Seahorse
        );

        this._subscription.add(
          this._seahorseModuleService.getAll(this.organisationId).subscribe({
            next: (modules) => {
              this.rows = (modules ?? []).map((module) => ({
                id: module.id,
                isActive: module.isActive,
                name: this._translateService.instant(
                  objects.find((obj) => obj.mappingKey === module.mappingKey)
                    ?.name ?? module.mappingKey
                ),
              }));
            },
            complete: () => {
              this.isLoading = false;
            },
          })
        );
      })
    );
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }
}
